/**
 * BusinessHoursInput — structured weekly-schedule editor for
 * client_info.office_hours_structured (Phase 4,
 * specs/completed/fb-branding-address-hours-fix.md). Fully controlled,
 * owns no persistence — it never calls updateProject, same reason
 * AddressAutocompleteInput doesn't: ProjectSettings PUTs client_info
 * wholesale (ProjectSettings.tsx's Save Changes).
 *
 * Reference component for the per-day row interaction:
 * Booking/AvailabilityEditorCore.tsx (day label column, type="time" pairs,
 * an X remove control, an "Add period" control) — but this is NOT a reuse
 * of that component (it allows unlimited windows per day; Facebook allows
 * at most 2) and does not import Booking.css. The visual container is the
 * Project Settings Contact Information card: ps-form-group / ps-label /
 * ps-field-hint tokens.
 *
 * The frontend never decides pushability — it only mirrors
 * business_hours.validate's per-range checks for inline guidance.
 * business_hours.is_pushable (backend) remains the push contract.
 */
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { GlassButton } from './GlassButton';
import { OfficeHoursStructured } from '../../services/api';
import './BusinessHoursInput.css';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<DayKey, string> = {
    mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
    fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT: Record<DayKey, string> = {
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

type Range = { open: string; close: string };

function defaultSchedule(): OfficeHoursStructured {
    return {
        version: 1,
        days: {
            mon: [{ open: '09:00', close: '17:00' }],
            tue: [{ open: '09:00', close: '17:00' }],
            wed: [{ open: '09:00', close: '17:00' }],
            thu: [{ open: '09:00', close: '17:00' }],
            fri: [{ open: '09:00', close: '17:00' }],
            sat: [],
            sun: [],
        },
        updated_at: new Date().toISOString(),
    };
}

// Per-range display-only validation, mirroring business_hours.validate's
// per-range rules (time order, overlap, overnight <= 06:00). The backend
// is the actual push contract — this is guidance only.
function validateDay(ranges: Range[]): string | null {
    if (ranges.length > 2) return 'At most 2 periods are allowed.';
    let prevClose: string | null = null;
    for (let i = 0; i < ranges.length; i++) {
        const { open, close } = ranges[i];
        if (!open || !close) return 'Each period needs both an open and close time.';
        const isOvernight = close < open;
        if (isOvernight) {
            if (close > '06:00') return "An overnight period can't close later than 06:00.";
            if (i !== ranges.length - 1) return 'Only the last period of the day can run overnight.';
        } else if (open >= close) {
            return 'The period must close after it opens.';
        }
        if (prevClose !== null && open < prevClose) return 'Second period starts before the first ends.';
        prevClose = close;
    }
    return null;
}

// Exported so the branding modal (Phase 6) can render the same summary
// format server-side logic produces (business_hours.format_summary) —
// presentation only, duplicated deliberately per the spec (not a
// pushability rule).
export function formatHoursSummary(value: OfficeHoursStructured | null | undefined): string[] {
    if (!value) return [];
    const perDay: Record<DayKey, string> = {} as Record<DayKey, string>;
    for (const day of DAY_KEYS) {
        const ranges = value.days[day] || [];
        if (ranges.length === 0) {
            perDay[day] = 'Closed';
        } else {
            const sorted = [...ranges].sort((a, b) => a.open.localeCompare(b.open));
            perDay[day] = sorted.map((r) => `${r.open}-${r.close}`).join(', ');
        }
    }
    const lines: string[] = [];
    let i = 0;
    while (i < DAY_KEYS.length) {
        const start = i;
        const val = perDay[DAY_KEYS[i]];
        while (i + 1 < DAY_KEYS.length && perDay[DAY_KEYS[i + 1]] === val) i += 1;
        const end = i;
        const label = start === end ? DAY_SHORT[DAY_KEYS[start]] : `${DAY_SHORT[DAY_KEYS[start]]}-${DAY_SHORT[DAY_KEYS[end]]}`;
        lines.push(`${label} ${val}`);
        i += 1;
    }
    return lines;
}

export interface BusinessHoursInputProps {
    value: OfficeHoursStructured | null;
    onChange: (next: OfficeHoursStructured | null) => void;
    disabled?: boolean;
}

export const BusinessHoursInput: React.FC<BusinessHoursInputProps> = ({ value, onChange, disabled = false }) => {
    const [confirmingRemove, setConfirmingRemove] = useState(false);

    const stamp = (next: OfficeHoursStructured): OfficeHoursStructured => ({
        ...next,
        updated_at: new Date().toISOString(),
    });

    const updateDay = (day: DayKey, ranges: Range[]) => {
        if (!value) return;
        onChange(stamp({ ...value, days: { ...value.days, [day]: ranges } }));
    };

    const toggleOpen = (day: DayKey, open: boolean) => {
        if (!value) return;
        updateDay(day, open ? [{ open: '09:00', close: '17:00' }] : []);
    };

    const addPeriod = (day: DayKey) => {
        if (!value) return;
        const ranges = value.days[day] || [];
        if (ranges.length >= 2) return;
        updateDay(day, [...ranges, { open: '13:00', close: '17:00' }]);
    };

    const removePeriod = (day: DayKey, idx: number) => {
        if (!value) return;
        const ranges = (value.days[day] || []).filter((_, i) => i !== idx);
        updateDay(day, ranges);
    };

    const updatePeriod = (day: DayKey, idx: number, field: 'open' | 'close', time: string) => {
        if (!value) return;
        const ranges = (value.days[day] || []).map((r, i) => (i === idx ? { ...r, [field]: time } : r));
        updateDay(day, ranges);
    };

    const copyMondayToWeekdays = () => {
        if (!value) return;
        const mondayRanges = value.days.mon || [];
        onChange(stamp({
            ...value,
            days: { ...value.days, tue: mondayRanges, wed: mondayRanges, thu: mondayRanges, fri: mondayRanges },
        }));
    };

    const handleRemove = () => {
        if (!confirmingRemove) {
            setConfirmingRemove(true);
            return;
        }
        if (window.confirm('Remove your structured business hours? You can set them up again later.')) {
            onChange(null);
        }
        setConfirmingRemove(false);
    };

    if (!value) {
        return (
            <div className="bh-root">
                <p className="bh-summary">No structured hours yet.</p>
                <div className="bh-actions">
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<Plus size={13} />}
                        onClick={() => onChange(stamp(defaultSchedule()))}
                        disabled={disabled}
                    >
                        Set up hours
                    </GlassButton>
                </div>
            </div>
        );
    }

    const summary = formatHoursSummary(value);

    return (
        <div className="bh-root">
            {DAY_KEYS.map((day) => {
                const ranges = value.days[day] || [];
                const isOpen = ranges.length > 0;
                const error = isOpen ? validateDay(ranges) : null;
                return (
                    <div key={day} className={`bh-day${error ? ' bh-row-invalid' : ''}`}>
                        <div className="bh-day-label">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isOpen}
                                    disabled={disabled}
                                    onChange={(e) => toggleOpen(day, e.target.checked)}
                                />
                                {' '}{DAY_LABELS[day]}
                            </label>
                        </div>
                        <div className="bh-periods">
                            {!isOpen && <span className="bh-closed">Closed</span>}
                            {ranges.map((r, idx) => (
                                <div className="bh-period" key={idx}>
                                    <input
                                        type="time"
                                        step={60}
                                        className="bh-time-input"
                                        aria-label={`${DAY_LABELS[day]}${idx === 1 ? ' second period' : ''} opens`}
                                        value={r.open}
                                        disabled={disabled}
                                        onChange={(e) => updatePeriod(day, idx, 'open', e.target.value)}
                                    />
                                    <span>–</span>
                                    <input
                                        type="time"
                                        step={60}
                                        className="bh-time-input"
                                        aria-label={`${DAY_LABELS[day]}${idx === 1 ? ' second period' : ''} closes`}
                                        value={r.close}
                                        disabled={disabled}
                                        onChange={(e) => updatePeriod(day, idx, 'close', e.target.value)}
                                    />
                                    <GlassButton
                                        variant="ghost"
                                        size="sm"
                                        icon={<X size={13} />}
                                        title="Remove period"
                                        onClick={() => removePeriod(day, idx)}
                                        disabled={disabled}
                                    />
                                </div>
                            ))}
                            {isOpen && ranges.length === 1 && (
                                <GlassButton
                                    variant="secondary"
                                    size="sm"
                                    icon={<Plus size={12} />}
                                    onClick={() => addPeriod(day)}
                                    disabled={disabled}
                                >
                                    Add second period
                                </GlassButton>
                            )}
                            {error && <div className="bh-error">{error}</div>}
                        </div>
                    </div>
                );
            })}

            <p className="bh-summary">{summary.join(' · ')}</p>

            <div className="bh-actions">
                <GlassButton variant="secondary" size="sm" onClick={copyMondayToWeekdays} disabled={disabled}>
                    Copy Monday to weekdays
                </GlassButton>
                <GlassButton variant="secondary" size="sm" onClick={handleRemove} disabled={disabled}>
                    Remove structured hours
                </GlassButton>
            </div>
        </div>
    );
};

export default BusinessHoursInput;
